import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { publicApi } from '@/lib/api';

const INTEREST_OPTIONS = [
    'Employment / Job Fairs',
    'Career Guidance',
    'Education Support',
    'Health Camps',
    'Environment / Tree Plantation',
    'Disaster Relief',
];

const AVAILABILITY = [
    { value: 'weekends', label: 'Weekends' },
    { value: 'weekdays', label: 'Weekdays' },
    { value: 'evenings', label: 'Evenings only' },
    { value: 'anytime', label: 'Anytime' },
];

const emptyState = {
    name: '',
    email: '',
    phone: '',
    city: '',
    district: '',
    skills: '',
    availability: '',
    interests: [],
    motivation: '',
};

const VolunteerForm = ({ compact = false }) => {
    const [form, setForm] = useState(emptyState);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleInterest = (v) => {
        setForm((f) => ({
            ...f,
            interests: f.interests.includes(v)
                ? f.interests.filter((x) => x !== v)
                : [...f.interests, v],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error('Please fill in name, email and phone.');
            return;
        }
        setSubmitting(true);
        try {
            await publicApi.submitVolunteer(form);
            setDone(true);
            setForm(emptyState);
            toast.success('Thank you! Our team will get in touch shortly.');
        } catch (err) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <Card className="bg-card border-border" data-testid="volunteer-form-success">
                <CardContent className="p-8 text-center">
                    <div className="h-14 w-14 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] mx-auto flex items-center justify-center mb-4">
                        <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <div className="font-display font-semibold text-xl mb-1">
                        You&apos;re on the team!
                    </div>
                    <p className="text-sm text-muted-foreground">
                        We&apos;ve received your details. Our volunteer coordinator will reach out
                        within 48 hours.
                    </p>
                    <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => setDone(false)}
                        data-testid="volunteer-form-reset"
                    >
                        Submit another registration
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <CardContent className="p-6">
                <form onSubmit={submit} className="space-y-4" data-testid="volunteer-registration-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="v-name">Full name *</Label>
                            <Input
                                id="v-name"
                                value={form.name}
                                onChange={(e) => update('name', e.target.value)}
                                placeholder="Your name"
                                required
                                data-testid="volunteer-input-name"
                            />
                        </div>
                        <div>
                            <Label htmlFor="v-phone">Phone *</Label>
                            <Input
                                id="v-phone"
                                value={form.phone}
                                onChange={(e) => update('phone', e.target.value)}
                                placeholder="+91 9xxxxxxxxx"
                                required
                                data-testid="volunteer-input-phone"
                            />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="v-email">Email *</Label>
                            <Input
                                id="v-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => update('email', e.target.value)}
                                placeholder="you@example.com"
                                required
                                data-testid="volunteer-input-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="v-city">City / District</Label>
                            <Input
                                id="v-city"
                                value={form.city}
                                onChange={(e) => update('city', e.target.value)}
                                placeholder="e.g. Chennai"
                                data-testid="volunteer-input-city"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Availability</Label>
                        <Select
                            value={form.availability}
                            onValueChange={(v) => update('availability', v)}
                        >
                            <SelectTrigger data-testid="volunteer-select-availability">
                                <SelectValue placeholder="How often can you volunteer?" />
                            </SelectTrigger>
                            <SelectContent>
                                {AVAILABILITY.map((a) => (
                                    <SelectItem key={a.value} value={a.value}>
                                        {a.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Areas of interest</Label>
                        <div className="mt-2 grid sm:grid-cols-2 gap-2">
                            {INTEREST_OPTIONS.map((opt) => (
                                <label
                                    key={opt}
                                    className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-[hsl(var(--secondary))]"
                                >
                                    <Checkbox
                                        checked={form.interests.includes(opt)}
                                        onCheckedChange={() => toggleInterest(opt)}
                                        data-testid={`volunteer-interest-${opt.slice(0, 8).toLowerCase().replace(/[^a-z]/g, '')}`}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {!compact && (
                        <div>
                            <Label htmlFor="v-skills">Skills you can contribute</Label>
                            <Input
                                id="v-skills"
                                value={form.skills}
                                onChange={(e) => update('skills', e.target.value)}
                                placeholder="e.g. teaching, medical, design, IT, driving..."
                                data-testid="volunteer-input-skills"
                            />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="v-motivation">Why do you want to volunteer?</Label>
                        <Textarea
                            id="v-motivation"
                            rows={3}
                            value={form.motivation}
                            onChange={(e) => update('motivation', e.target.value)}
                            placeholder="A few sentences about your motivation."
                            data-testid="volunteer-input-motivation"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold w-full sm:w-auto"
                        data-testid="volunteer-submit-button"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-2" /> Register as Volunteer
                            </>
                        )}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        We respect your privacy. Details are used only for volunteer coordination.
                    </p>
                </form>
            </CardContent>
        </Card>
    );
};

export default VolunteerForm;
